import type { SuretyAccountId, SuretyBondId, SuretyQuoteId } from "@inrev/common";
import { useAuthenticatedUser } from "@portal/providers/AuthenticatedUserProvider";
import { useCallback, useRef } from "react";
import { useDebouncedCallback } from "use-debounce";
import {
	type SearchRelationType,
	type SearchResultSource,
	type SearchSurface,
	trackSearchAbandoned,
	trackSearchPerformed,
	trackSearchResultClicked,
} from "../../analytics";
import { useAbandonOnUnmount } from "./useAbandonOnUnmount";

const SEARCH_PERFORMED_DEBOUNCE_MS = 2000;

export type SearchResultClickedHandler = (
	relationType: SearchRelationType,
	entityId: SuretyAccountId | SuretyQuoteId | SuretyBondId,
	resultSource: SearchResultSource,
) => void;

/**
 * Owns Performed / Result Clicked / Abandoned for one search surface so the
 * modal and dashboard report the same funnel. Abandoned fires on unmount when
 * a term is present and no result was clicked — including cmd+K dismiss,
 * which never went through the modal close handler.
 *
 * Search Opened is not owned here: it fires when the modal is opened, which
 * the dashboard panel never does.
 */
export const useSearchAnalytics = (surface: SearchSurface) => {
	const { user } = useAuthenticatedUser();
	const searchTermRef = useRef("");
	const convertedRef = useRef(false);
	const abandonedRef = useRef(false);
	const userType = user.type;

	// use-debounce drops a pending call on unmount rather than flushing it, so
	// Performed never lands after Abandoned.
	const trackPerformed = useDebouncedCallback((searchTerm: string) => {
		trackSearchPerformed(userType, searchTerm, surface);
	}, SEARCH_PERFORMED_DEBOUNCE_MS);

	const onSearchTermChange = useCallback(
		(searchTerm: string) => {
			searchTermRef.current = searchTerm;
			if (!searchTerm) {
				trackPerformed.cancel();
				return;
			}
			trackPerformed(searchTerm);
		},
		[trackPerformed],
	);

	const onResultClicked = useCallback<SearchResultClickedHandler>(
		(relationType, entityId, resultSource) => {
			convertedRef.current = true;
			trackPerformed.cancel();
			trackSearchResultClicked(relationType, entityId, surface, resultSource);
		},
		[surface, trackPerformed],
	);

	useAbandonOnUnmount(() => {
		if (convertedRef.current || abandonedRef.current || !searchTermRef.current) return;
		abandonedRef.current = true;
		trackSearchAbandoned(userType, searchTermRef.current, surface);
	});

	return { onSearchTermChange, onResultClicked };
};

export type SearchAnalytics = ReturnType<typeof useSearchAnalytics>;
